/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * Program list — the module landing. Create a new draft, open an existing
 * program, copy-from-prior, or export any program's PDF straight from the row.
 */
import { useEffect, useState, useCallback } from "react";
import { listPrograms, createProgram, copyProgram, getProgram } from "../utils/incentiveService.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import HomeButton from "../../../shared/components/HomeButton.jsx";
import { exportProgramPdf } from "../utils/incentivePdf.js";
import StatusBadge from "./StatusBadge.jsx";
import "./incentive.css";

function fmtRange(a, b) {
  if (!a && !b) return "No dates set";
  const f = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
  return `${f(a)} – ${f(b)}`;
}

export default function ProgramListPage({ onHome, onOpen, onDistributorProgress }) {
  const [programs, setPrograms] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exportingId, setExportingId] = useState(null);
  const [account, setAccount] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      setPrograms(await listPrograms());
    } catch (e) { setErr(e.message); setPrograms([]); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getMyPrimaryAccount().then((a) => setAccount(a || null)).catch(() => {}); }, []);

  async function handleExport(e, id) {
    e.stopPropagation();
    setExportingId(id); setErr(null);
    try {
      const acct = account || (await getMyPrimaryAccount());
      const { program, goals, scores } = await getProgram(id);
      await exportProgramPdf({
        program, goals, scores,
        accountName: acct?.name || "", logoUrl: acct?.logoUrl || "",
        primaryColor: acct?.primaryColor || "", secondaryColor: acct?.secondaryColor || "",
      });
    } catch (err) {
      console.error("[incentive] row export", err);
      setErr(`Could not generate the PDF${err?.message ? `: ${err.message}` : "."}`);
    } finally { setExportingId(null); }
  }

  async function handleNew() {
    setBusy(true); setErr(null);
    try {
      const id = await createProgram({ name: "Untitled incentive program" });
      onOpen(id);
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  async function handleCopy(e, id) {
    e.stopPropagation();
    setBusy(true); setErr(null);
    try {
      const newId = await copyProgram(id);
      onOpen(newId);
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  return (
    <section className="settings-card wide">
      <HomeButton onClick={onHome} />

      <div className="it-head" style={{ marginTop: 10 }}>
        <h1>Incentive Tracker</h1>
        <div className="it-actions">
          {/* Item 232 — the per-distributor progress view lives here. */}
          <button className="secondary" onClick={() => onDistributorProgress?.()}>Distributor progress</button>
          <button className="btn" onClick={handleNew} disabled={busy}>+ New program</button>
        </div>
      </div>

      {err && <p className="error">{err}</p>}

      {programs === null ? (
        <p className="muted">Loading…</p>
      ) : programs.length === 0 ? (
        <div className="it-empty">
          <p>No incentive programs yet.</p>
          <p className="muted">Create one to build distributor goals, send it for sign-off, and score it at month-end.</p>
        </div>
      ) : (
        <div className="it-list">
          {programs.map((p) => (
            <div key={p.id} className="it-row" role="button" tabIndex={0}
                 onClick={() => onOpen(p.id)}
                 onKeyDown={(e) => { if (e.key === "Enter") onOpen(p.id); }}>
              <div className="it-row-main">
                <div className="it-row-name">{p.name}</div>
                <div className="it-row-meta">
                  {Array.isArray(p.target_states) && p.target_states.length > 0 && (
                    <span>{p.target_states.join(", ")}</span>
                  )}
                  <span>{p.distributor_name || "No distributor"}</span>
                  <span>{fmtRange(p.start_date, p.end_date)}</span>
                  <span>{p.goal_count} goal{p.goal_count === 1 ? "" : "s"}</span>
                  {/* Item 230 — a program that says "Sent" but never left the
                      building says so here, not only once you open it. */}
                  {p.send_status === "failed" && <span className="it-undelivered">Not emailed</span>}
                  {p.send_status === "sent" && p.sent_to && <span>Emailed to {p.sent_to}</span>}
                </div>
              </div>
              <div className="it-row-side">
                <StatusBadge status={p.status} />
                <button className="secondary" onClick={(e) => handleExport(e, p.id)}
                        disabled={exportingId === p.id || p.goal_count === 0}
                        title={p.goal_count === 0 ? "Add a goal first" : "Export PDF"}>
                  {exportingId === p.id ? "…" : "Export PDF"}
                </button>
                <button className="secondary" onClick={(e) => handleCopy(e, p.id)} disabled={busy} title="Copy to a new draft">Copy</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
